import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, output } from '@angular/core';
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { type AgentStatusConfig } from '../../agent.types';
import { StitchyActivityService } from '../../services/stitchy-activity.service';
import { AttentionDotComponent } from '../attention-dot/attention-dot.component';

const HEAD_SIZE = { nav: 28, fab: 56 } as const;
const STATUS_ICON_SIZE = { nav: 6, fab: 8 } as const;

@Component({
  selector: 'app-stitchy-launcher',
  standalone: true,
  imports: [IconComponent, AttentionDotComponent],
  templateUrl: './stitchy-launcher.component.html',
  styleUrl: './stitchy-launcher.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StitchyLauncherComponent {
  public readonly variant = input<'nav' | 'fab'>('fab');
  public readonly unseen = input<boolean>(false);
  /** A state the host knows better, such as the Free Trial teaser's Paid plans (SBUX-1231); null shows Stitchy's own. */
  public readonly status = input<AgentStatusConfig | null>(null);
  public readonly clicked = output<void>();

  private readonly injector = inject(Injector);

  /** Stitchy's state while the panel is away, as the window bar's pill shows it (SBUX-1112). Read only when shown. */
  protected readonly activity = computed<AgentStatusConfig>(
    () => this.status() ?? this.injector.get(StitchyActivityService).config(),
  );

  protected readonly statusIconSize = computed(() => STATUS_ICON_SIZE[this.variant()]);

  protected readonly headSize = computed(() => HEAD_SIZE[this.variant()]);

  /** The excited pose bobs and wags; the calm head is the resting state. */
  protected readonly head = computed<Icon>(() => (this.unseen() ? 'stitchy-excited' : 'stitchy-head'));

  /** The pose and the dot are the sighted cue; the name carries the same state to a screen reader. */
  protected readonly label = computed<string>(() => {
    const restore = `Restore Stitchy · ${this.activity().label}`;
    return this.unseen() ? `${restore}, something needs your attention` : restore;
  });
}
